import React, { useCallback, useContext, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import Tooltip from '@totem/components/common/tooltip/Tooltip';
import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import NinjaOneContext from '@totem/components/settings/sections/ninjaOneMapping/NinjaOneContext';
import {
  Location,
  TenantData,
} from '@totem/components/settings/sections/ninjaOneMapping/reportTypes';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { NINJA_ONE_ENDPOINT } from '@totem/utilities/endpoints';

const styles = {
  iconButton: {
    margin: '0 2px',
  },
};

type Props = {
  tenant: TenantData;
  locations: Location[];
};

const NinjaOneLocationMappingTable = ({ tenant, locations }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { loading, onLocationAction, onActionComplete } =
    useContext(NinjaOneContext);
  const [isSending, setIsSending] = useState(false);

  const sendTenantDelete = useCallback(
    async (apiConfigId: string, tenantId: number, locationId: number) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      fetch(
        `${NINJA_ONE_ENDPOINT}/tenants/${apiConfigId}/${tenantId}/${locationId}`,
        {
          method: 'DELETE',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      )
        .then((res) => {
          if (res.status < 400) {
            onActionComplete(true);
          } else {
            notification.error({
              title: 'Error location delete!',
              description: 'An error occurred while deleting location.',
              duration: 0,
            });

            onActionComplete(false);
          }
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [isSending],
  );

  const onDelete = (location: Location) => {
    sendTenantDelete(
      tenant.apiConfigId,
      tenant.tenant.tenantId,
      location.locationId,
    );
  };

  const columns: ColumnProps<Location>[] = [
    {
      title: 'Location Name (In NinjaOne)',
      dataIndex: 'LocationName',
      key: 'LocationName',
      sorter: (compA, compB) =>
        sortStringAscending(compA.locationName, compB.locationName),
      render: (text: string, item: Location) => {
        return item.locationName;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (compA, compB) =>
        sortStringAscending(compA.description, compB.description),
      render: (text: string, item: Location) => {
        return item.description;
      },
    },
    {
      title: 'Building',
      dataIndex: 'building.id',
      key: 'building.id',
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
      render: (text: string, item: Location) => {
        return typeof item.building !== 'undefined' && item.building !== null
          ? item.building.name
          : '';
      },
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'action',
      render: (text, item: Location) => (
        <div className="center-table-cell">
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Update Building Mapping" placement="top">
              <Button
                icon={<EditOutlined />}
                shape="circle"
                onClick={(): void =>
                  onLocationAction('showLocationMappingDialog', tenant, item)
                }
                style={styles.iconButton}
              />
            </Tooltip>
          )}
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Delete Location" placement="top">
              <Button
                icon={<IBDIcon name={'Delete'} />}
                shape="circle"
                onClick={(): void => onDelete(item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={
        typeof locations !== 'undefined' && locations !== null ? locations : []
      }
      loading={loading}
      rowKey={(record) => record.locationId}
    />
  );
};

export default NinjaOneLocationMappingTable;
